import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { Bell, BellOff, Trash2, Plus, MapPin, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';

interface Zone {
  id: string;
  name: string;
  center_lat: number;
  center_lng: number;
  radius_miles: number;
  min_hail_inches: number;
  min_wind_mph: number;
  notify_email: boolean;
  is_active: boolean;
  last_triggered_at: string | null;
  created_at: string;
}

export function AlertZonesTab() {
  const { user, orgId } = useAuthContext();
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  // form state
  const [form, setForm] = useState({
    name: '',
    address: '',
    radius_miles: 25,
    min_hail_inches: 0.75,
    min_wind_mph: 60,
    notify_email: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    load();
  }, [orgId]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('storm_alert_zones')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) toast.error(error.message);
    else setZones((data || []) as Zone[]);
    setLoading(false);
  };

  const createZone = async () => {
    if (!orgId || !form.name.trim() || !form.address.trim()) {
      toast.error('Name and address required');
      return;
    }
    setSaving(true);
    try {
      // Geocode via address-storm-history endpoint (it geocodes for free)
      const { data: geoData, error: geoError } = await supabase.functions.invoke('address-storm-history', {
        body: { address: form.address, days: 1, radiusMiles: 1 },
      });
      if (geoError || !geoData?.summary) throw new Error('Could not geocode address');
      const { latitude, longitude, address } = geoData.summary;

      const { error } = await supabase.from('storm_alert_zones').insert({
        org_id: orgId,
        created_by: user?.id,
        name: form.name,
        center_lat: latitude,
        center_lng: longitude,
        radius_miles: form.radius_miles,
        min_hail_inches: form.min_hail_inches,
        min_wind_mph: form.min_wind_mph,
        notify_email: form.notify_email,
      });
      if (error) throw error;
      toast.success(`Zone created for ${address}`);
      setOpen(false);
      setForm({ name: '', address: '', radius_miles: 25, min_hail_inches: 0.75, min_wind_mph: 60, notify_email: true });
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (zone: Zone) => {
    const { error } = await supabase
      .from('storm_alert_zones')
      .update({ is_active: !zone.is_active })
      .eq('id', zone.id);
    if (error) toast.error(error.message);
    else load();
  };

  const deleteZone = async (id: string) => {
    const { error } = await supabase.from('storm_alert_zones').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Zone deleted'); load(); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <div className="font-semibold">Storm Alert Zones</div>
            <div className="text-xs text-muted-foreground">Get notified when hail or severe wind hits your service areas</div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2"><Plus className="w-4 h-4" /> New Zone</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Alert Zone</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Zone Name</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="North Dallas Service Area" />
                </div>
                <div>
                  <Label>Center Address</Label>
                  <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Dallas, TX" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label>Radius (mi)</Label>
                    <Input type="number" value={form.radius_miles} onChange={e => setForm(f => ({ ...f, radius_miles: Number(e.target.value) }))} />
                  </div>
                  <div>
                    <Label>Min Hail (")</Label>
                    <Input type="number" step="0.25" value={form.min_hail_inches} onChange={e => setForm(f => ({ ...f, min_hail_inches: Number(e.target.value) }))} />
                  </div>
                  <div>
                    <Label>Min Wind (mph)</Label>
                    <Input type="number" value={form.min_wind_mph} onChange={e => setForm(f => ({ ...f, min_wind_mph: Number(e.target.value) }))} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email notifications</Label>
                  <Switch checked={form.notify_email} onCheckedChange={c => setForm(f => ({ ...f, notify_email: c }))} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={createZone} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Zone'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></CardContent></Card>
      ) : zones.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No zones yet. Create one to start monitoring.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {zones.map(z => (
            <Card key={z.id} className={z.is_active ? '' : 'opacity-60'}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold flex items-center gap-2">
                      {z.is_active ? <Bell className="w-4 h-4 text-primary" /> : <BellOff className="w-4 h-4 text-muted-foreground" />}
                      {z.name}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {z.center_lat.toFixed(3)}, {z.center_lng.toFixed(3)}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(z)}>{z.is_active ? 'Pause' : 'Resume'}</Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteZone(z.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline">{z.radius_miles} mi radius</Badge>
                  <Badge variant="outline">Hail ≥ {z.min_hail_inches}"</Badge>
                  <Badge variant="outline">Wind ≥ {z.min_wind_mph} mph</Badge>
                  {z.notify_email && <Badge variant="outline" className="gap-1"><Mail className="w-3 h-3" /> Email</Badge>}
                </div>
                {z.last_triggered_at && (
                  <div className="text-xs text-muted-foreground">Last triggered: {new Date(z.last_triggered_at).toLocaleString()}</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
